import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@material-ui/icons/Close";
import { Grid, makeStyles, Box, Typography, Dialog } from "@material-ui/core";

import { api } from "../../services";
import { PortfolioTable } from "./PortfolioTable";
import { EXCEL_FILE_TYPE } from "../../constants";
import { header } from "../workspace/settings/sample";
import SettingHeader from "../../components/SettingHeader";
import { MEDIA_SERVICE_FILES_BASE_URL } from "../../services/api";
import downloadIcon from "../../assets/images/icons/download.svg";
import { saveNotification } from "../../redux/notification/actions";
import { Button, TextButton, UploadButton } from "../../components/base";

export const useStyles = makeStyles((theme) => ({
  headers: {
    margin: "0 50px",
    padding: "40px 0 80px",
  },
  saveChanges: {
    margin: "8px 14px 8px 0",
  },
  downloadTemplateBtn: {
    margin: "8px 0px 8px 14px",
  },
  popupContent: {
    padding: `${theme.spacing(4)}px ${theme.spacing(1)}px`,
    lineHeight: "2.5rem",
  },
  approveBtn: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    marginLeft: theme.spacing(5),
  },
}));

export const GLOBAL_DRAFT_URL = "/onboarding/g/portfolio-registration/draft/";
export const DEFAULT_DRAFT_URL = "/onboarding/d/portfolio-registration/draft/";
export const DEFAULT_TEMPLATE_FILE = "default_portfolio_registration_template.xlsx";

export const PortfolioRegistrationPage = ({
  globalDraftBaseUrl = GLOBAL_DRAFT_URL,
  draftBaseUrl = DEFAULT_DRAFT_URL,
  Table = PortfolioTable,
  templateFile = DEFAULT_TEMPLATE_FILE,
  callbackAfterUpload,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingData, setUploadingData] = useState(false);
  const [draft, setDraft] = useState();
  const [selectedFile, setSelectedFile] = useState();
  const [showPopup, setShowPopup] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const rowsRef = useRef();
  rowsRef.current = rows;

  useEffect(() => {
    api
      .get(globalDraftBaseUrl, { params: { clusters: user.clusters[0].id } })
      .then((resp) => resp.data)
      .then((draft) => {
        setDraft(draft);
        if (draft.buildings) setRows(draft.buildings);
      });
  }, []);

  const onFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    if (draft?.files?.length) {
      setShowPopup(true);
    } else {
      uploadFile(file);
    }
  };

  const uploadFile = (file = null) => {
    if (uploading) return;
    const form = new FormData();
    form.append("file", file || selectedFile);
    setUploading(true);
    return api
      .post(`${draftBaseUrl}upload/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { clusters: user.clusters[0].id },
      })
      .then((res) => res.data)
      .then((data) => {
        setRows(data.buildings);
        setDraft({
          ...draft,
          files: [
            ...(draft?.files || []),
            {
              id: data.id,
              name: data.name,
              ext: data.ext,
              size: data.size,
              file: data.file,
              created: data.created,
            },
          ],
        });
        if (callbackAfterUpload) {
          callbackAfterUpload(setRows, setShowMessage);
        }
        setShowMessage(true);
      })
      .catch((err) => {
        const statusCode = err?.response?.status;
        let message;
        if (statusCode === 400) {
          if ("file" in err.response.data) {
            message = err.response.data["file"][0];
          } else if ("error" in err.response.data) {
            message = err.response.data["error"];
          }
        } else if (statusCode === 500) {
          message =
            "Error when processing the uploaded file. Make sure the uploaded file matched with template";
        } else {
          message = "Something went wrong. Please try again";
        }
        dispatch(saveNotification({ message, error: true }));
      })
      .finally(() => {
        setUploading(false);
        setShowPopup(false);
      });
  };

  const saveChanges = () => {
    if (uploadingData) return;
    setUploadingData(true);
    api
      .post(`${draftBaseUrl}save/`, rowsRef.current, {
        params: { clusters: user.clusters[0].id },
      })
      .then((resp) => resp.data)
      .then((draft) => {
        setRows(draft.buildings);
        setShowMessage(true);
        if (callbackAfterUpload) {
          callbackAfterUpload(setRows, setShowMessage);
        }
      })
      .catch((e) => {
        let message = "Oops...Something went wrong.";
        if (e.response && "error" in e.response.data) {
          message = e.response.data.error;
        } else if (e.response.data instanceof Array) {
          message = "Some fields are required";
        }
        dispatch(
          saveNotification({
            message: message,
            error: true,
          })
        );
      })
      .finally(() => {
        setUploadingData(false);
      });
  };

  const deleteBuilding = (id) => {
    api
      .delete(`${globalDraftBaseUrl}buildings/${id}/`, {
        params: { clusters: user.clusters[0].id },
      })
      .catch(() => {
        dispatch(
          saveNotification({
            message: "Oops...Something went wrong.",
            error: true,
          })
        );
      });
  };

  return (
    <Box className={classes.headers}>
      <SettingHeader
        title={header.portfolioregistration.title}
        subtitle={header.portfolioregistration.subtitle}
        img={header.portfolioregistration.img}
      />
      <Grid container justifyContent="flex-end">
        <Button
          text="SAVE CHANGES"
          disabled={!draft?.is_table_enabled}
          onClick={saveChanges}
          loading={uploadingData}
          rootStyle={classes.saveChanges}
        />
        <UploadButton
          accept={EXCEL_FILE_TYPE}
          loading={uploading}
          onChange={onFileChange}
          text={draft?.files?.length === 0 ? "UPLOAD THE PORTFOLIO" : "UPLOAD NEW VERSION"}
          disabled={!draft?.is_table_enabled}
        />

        <Button
          text="DOWNLOAD TEMPLATE"
          prefixIcon={downloadIcon}
          disabled={!draft?.is_table_enabled}
          rootStyle={classes.downloadTemplateBtn}
          href={`${MEDIA_SERVICE_FILES_BASE_URL}/${templateFile}`}
        />
      </Grid>
      <Table
        rows={rows}
        setRows={setRows}
        disableTable={!draft?.is_table_enabled}
        deleteBuilding={deleteBuilding}
        showMessage={showMessage}
        setShowMessage={setShowMessage}
      />

      <Dialog open={showPopup} maxWidth="sm">
        <Box mx={5} my={4}>
          <Grid container justifyContent="space-between">
            <Typography variant="h3">Portfolio Registration File Upload</Typography>
            <CloseIcon onClick={() => setShowPopup(false)} />
          </Grid>

          <Typography className={classes.popupContent} align="center" variant="h2">
            If you proceed with uploading this excel file, the previously uploaded file alongside
            with the table data will be erased. Are you sure you want to continue?
          </Typography>

          <Grid container justifyContent="flex-end">
            <TextButton text="CANCEL" onClick={() => setShowPopup(false)} />
            <Button
              text="APPROVE"
              loading={uploading}
              onClick={() => uploadFile()}
              rootStyle={classes.approveBtn}
            />
          </Grid>
        </Box>
      </Dialog>
    </Box>
  );
};
